import React from 'react';
import { datetime } from '@common/services/datetime';
import { CreateShiftForm, CreateShiftFormValues } from './create-shift-form';
import { Button, ButtonGroup, Drawer, Form } from '@common/components';
import { useMutation, useNavigate } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import { CREATE_SHIFT_MUTATION } from '@scheduling/graphql';
import { buildDialogUrl } from '@common/services';
import { useCurrentMembership } from '@identity/hooks';

export function CreateShiftDrawer({
  open,
  onClose,
  startDate,
}: {
  open: boolean;
  onClose: () => void;
  startDate?: string;
}) {
  const navigate = useNavigate();
  const currentMembership = useCurrentMembership();

  const [form] = Form.useForm();

  const showErrorMessage = () => {
    createMessage(
      'error',
      `Failed to create Shift. Please try again or contact your hiring partner.`
    );
  };

  const buildShiftDetailsDrawerUrl = (shiftId: string) =>
    buildDialogUrl('shifts-request-details-drawer', {
      overlap: false,
      props: {
        shiftId,
      },
    });

  const [createShift, { loading }] = useMutation(CREATE_SHIFT_MUTATION, {
    onCompleted: (result) => {
      if (
        result?.createShiftGap === null ||
        Number(result?.createShiftGap?.errors?.length) > 0
      ) {
        showErrorMessage();
      } else {
        createMessage('success', 'Shift has been created!');

        const drawerUrl = buildShiftDetailsDrawerUrl(
          result?.createShiftGap?.shiftGap?.id || ''
        );

        navigate(drawerUrl);
      }
    },
    onError: showErrorMessage,
    refetchQueries: [
      'PaginatedShiftGapOpenings',
      'PaginatedShiftGapOpeningsCount',
      'ShiftsCountByDay',
    ],
  });

  const onCloseDrawer = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = (values: CreateShiftFormValues) => {
    // NOTE: We're stripping the timezone here because this is in facility local time
    const stripTimezone = (startTime: string) =>
      datetime(startTime, ['HH:mm']).format('YYYY-MM-DDTHH:mm:ss');

    const duration =
      values.shiftDuration.length > 1
        ? values.shiftDuration[1]
        : values.shiftDuration[0];
    const shiftDuration = Number(duration).toFixed(2);

    const input = {
      organizationUnitId: values.organizationUnitId,
      roleId: values.roleId,
      shiftDuration: Number(shiftDuration),
      startDate: values.startDate,
      startTime: stripTimezone(values.startTime),
      openings: values.openings,
      createdById: currentMembership.worksUser.id || '',
      shiftGapTagIds: values.shiftGapTagIds,
    };

    createShift({
      variables: {
        input,
      },
    });
  };

  return (
    <>
      <Drawer
        open={open}
        className="upsert-shift-request-drawer"
        title="Create Shift"
        onClose={onCloseDrawer}
        footer={
          <Footer
            loading={loading}
            onClose={onCloseDrawer}
            onCreateShift={form.submit}
          />
        }
      >
        <CreateShiftForm
          form={form}
          handleSubmit={handleSubmit}
          startDate={startDate}
        />
      </Drawer>
      <style jsx>{`
        @media (min-width: 1281px) {
          :global(.upsert-shift-request-drawer .ant-drawer-content-wrapper) {
            width: 45vw !important;
          }
        }
        @media (max-width: 1280px) {
          :global(.upsert-shift-request-drawer .ant-drawer-content-wrapper) {
            width: 70vw !important;
          }
        }
      `}</style>
    </>
  );
}

const Footer = ({
  loading,
  onClose,
  onCreateShift,
}: {
  loading: boolean;
  onClose: () => void;
  onCreateShift: () => void;
}) => (
  <ButtonGroup>
    <Button onClick={onClose}>Cancel</Button>
    <Button kind="primary" loading={loading} onClick={onCreateShift}>
      Create Shift
    </Button>
  </ButtonGroup>
);
